import { formatMoney } from './currency-format';
import { THEMES } from './appearance';
import { isClosed, journalMetrics, localDate, number } from './journal-metrics';

export const shareTheme = id => THEMES[id] || Object.values(THEMES).find(theme => theme.card === id) || THEMES.classic;
export const profileSlug = name => (name || '').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'');
export const signedNumber = value => value === 0 ? '0' : `${value < 0 ? '−' : '+'}${Math.abs(value).toFixed(2)}`;
export const percent = value => `${signedNumber(value)}%`;
export function tradeR(trade) {
  if (!isClosed(trade)) return null;
  if (trade.result === 'BE') return 0;
  const hasReturn = trade.returnPct != null && trade.returnPct !== '' && Number.isFinite(Number(trade.returnPct));
  if (hasReturn && number(trade.riskPct)) return number(trade.returnPct) / Math.abs(number(trade.riskPct));
  return trade.rr != null && trade.rr !== '' && Number.isFinite(Number(trade.rr)) ? Math.abs(number(trade.rr)) * (trade.result === 'LOSS' ? -1 : 1) : null;
}
export function tradeHero(trade, unit = '%', accountSize = 0, currency = 'USD') {
  if (!isClosed(trade)) return {value:'—',unit:''};
  if (unit === 'R') { const r = tradeR(trade); return { value: r == null ? '—' : signedNumber(r), unit: r == null ? '' : 'R' }; }
  const valid = trade.returnPct != null && trade.returnPct !== '' && Number.isFinite(Number(trade.returnPct));
  if (!valid && trade.result !== 'BE') return {value:'—',unit:''};
  const value = trade.result === 'BE' ? 0 : number(trade.returnPct);
  if (unit === 'currency' && accountSize > 0) return { value: moneyReturn(value,accountSize,currency), unit:'' };
  return {value:signedNumber(value),unit:'%'};
}
export function moneyReturn(value, size, currency) {
  return formatMoney(size * value / 100, currency, { signed: true });
}
const prettyDate = date => date.toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'});
export function sharePeriod(type, anchor = localDate()) {
  let date = new Date(`${anchor.slice(0,10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) date = new Date();
  let start = new Date(date), end = new Date(date), label = 'All-time performance';
  if (type === 'day') { label = prettyDate(date); }
  else if (type === 'week') { start.setDate(date.getDate() - (date.getDay() + 6) % 7); end = new Date(start); end.setDate(start.getDate() + 6); label = `Week of ${prettyDate(start)}`; }
  else if (type === 'month') { start = new Date(date.getFullYear(),date.getMonth(),1,12); end = new Date(date.getFullYear(),date.getMonth()+1,0,12); label = date.toLocaleDateString('en-GB',{month:'long',year:'numeric'}); }
  else if (type === 'quarter') { const q = Math.floor(date.getMonth()/3); start = new Date(date.getFullYear(),q*3,1,12); end = new Date(date.getFullYear(),q*3+3,0,12); label = `Q${q+1} · ${date.getFullYear()}`; }
  else if (type === 'year') { start = new Date(date.getFullYear(),0,1,12); end = new Date(date.getFullYear(),11,31,12); label = `${date.getFullYear()} · Annual review`; }
  else return {type:'all',start:'',end:'',label};
  return {type,start:localDate(start),end:localDate(end),label};
}
export const tradesInPeriod = (trades, period) => period.type === 'all' ? trades : trades.filter(trade => trade.date >= period.start && trade.date <= period.end);
export function shareMetrics(trades) {
  const stats = journalMetrics(trades.map(trade => trade.result === 'BE' ? { ...trade, returnPct: 0 } : trade));
  const wins = stats.closed.filter(t=>t.result==='WIN'), losses = stats.closed.filter(t=>t.result==='LOSS');
  const rValues = wins.map(tradeR).filter(value=>value!=null);
  return {...stats, avgR:rValues.length?rValues.reduce((a,b)=>a+b,0)/rValues.length:null,
    avgWin:wins.length?wins.reduce((sum,t)=>sum+number(t.returnPct),0)/wins.length:null,
    avgLoss:losses.length?losses.reduce((sum,t)=>sum+number(t.returnPct),0)/losses.length:null,
    months:[...new Set(stats.closed.map(t=>t.date?.slice(0,7)).filter(Boolean))].sort().map(month=>({month,value:stats.closed.filter(t=>t.date.startsWith(month)).reduce((sum,t)=>sum+number(t.returnPct),0)})),
  };
}
export const periodFilename = period => period.type === 'all' ? 'all-time' : `${period.type}-${period.start}`;

export async function captureShare(node, backgroundColor) {
  if (!node) throw new Error('The card is not ready yet.');
  if (document.fonts?.ready) await document.fonts.ready;
  await Promise.all(Array.from(node.querySelectorAll('img')).map(img => {
    if (img.complete) return Promise.resolve();
    return new Promise(resolve => {
      const done = () => { clearTimeout(timer); img.removeEventListener('load',done); img.removeEventListener('error',done); resolve(); };
      const timer = setTimeout(done,5000); img.addEventListener('load',done); img.addEventListener('error',done);
    });
  }));
  const html2canvas = (await import('html2canvas')).default;
  return html2canvas(node,{scale:2,useCORS:true,logging:false,backgroundColor});
}
export function downloadCanvas(canvas, filename) {
  const link = document.createElement('a');link.download=filename;link.href=canvas.toDataURL('image/png');link.click();
}

export async function downloadPerformancePDF(canvas, {trades, period, name, palette, filename, signal}) {
  const jsPDF = (await import('jspdf')).default;
  if (signal?.aborted) throw new DOMException('Export cancelled.', 'AbortError');
  const pdf = new jsPDF({orientation:'portrait',unit:'mm',format:'a4'});
  const pageW=210,pageH=297,margin=15,width=pageW-margin*2;
  const height=Math.min(250,canvas.height*width/canvas.width);
  pdf.setFillColor(palette.bg);pdf.rect(0,0,pageW,pageH,'F');
  const imageWidth = Math.min(width, height * canvas.width / canvas.height);
  pdf.addImage(canvas.toDataURL('image/png'),'PNG',(pageW-imageWidth)/2,margin,imageWidth,height);
  pdf.setFontSize(9);pdf.setTextColor(palette.t3);pdf.text('Performance from closed trades. The trade list follows.',margin,Math.min(282,margin+height+12));
  if(height<180){
    const stats=shareMetrics(trades), top=margin+height+31;
    pdf.setFontSize(10);pdf.setTextColor(palette.t3);pdf.text('AVERAGE WIN',15,top);pdf.text('AVERAGE LOSS',110,top);
    pdf.setFontSize(20);pdf.setTextColor(palette.positive);pdf.text(stats.avgWin==null?'—':percent(stats.avgWin),15,top+12);
    pdf.setTextColor(palette.negative);pdf.text(stats.avgLoss==null?'—':percent(stats.avgLoss).replace('−','-'),110,top+12);
    if(['quarter','year'].includes(period.type)){
      const start=new Date(`${period.start}T12:00:00`),count=period.type==='year'?12:3;
      const months=Array.from({length:count},(_,i)=>{const date=new Date(start.getFullYear(),start.getMonth()+i,1,12),key=localDate(date).slice(0,7);return {label:date.toLocaleDateString('en-GB',{month:'short'}),value:stats.months.find(m=>m.month===key)?.value||0};});
      const max=Math.max(1,...months.map(m=>Math.abs(m.value))),base=top+62,gap=width/months.length;
      pdf.setFontSize(9);pdf.setTextColor(palette.t3);pdf.text('MONTHLY RETURNS (%)',15,top+28);
      pdf.setDrawColor(palette.s3);pdf.line(15,base,195,base);
      months.forEach((m,i)=>{const x=15+i*gap+gap*.18,barW=gap*.64,barH=Math.abs(m.value)/max*24;
        pdf.setFillColor(m.value<0?palette.negative:palette.positive);pdf.rect(x,m.value<0?base:base-barH,barW,barH,'F');
        pdf.setTextColor(palette.t3);pdf.setFontSize(8);pdf.text(m.label,x,base+34);pdf.text(`${m.value.toFixed(1)}%`,x,base+40);
      });
    }
  }
  let y=0,page=1;
  const header=()=>{
    pdf.addPage();page++;pdf.setFillColor(palette.bg);pdf.rect(0,0,pageW,pageH,'F');
    pdf.setTextColor(palette.t1);pdf.setFontSize(17);pdf.text('TRADELOG / JOURNAL',margin,22);
    pdf.setFontSize(10);pdf.setTextColor(palette.t3);pdf.text(`${period.label} | ${name || 'Trader'}`,margin,30);
    y=43;pdf.setFontSize(8);['DATE','INSTRUMENT','SIDE','RESULT','R','RETURN'].forEach((label,i)=>pdf.text(label,[15,45,92,113,148,172][i],y));y+=5;
    pdf.setDrawColor(palette.s3);pdf.line(margin,y,pageW-margin,y);y+=9;
    pdf.setFontSize(8);pdf.text(`trade-log.co.uk | ${page}`,margin,287);
  };
  const rows=[...trades].sort((a,b)=>(a.date||'').localeCompare(b.date||''));
  header();
  for(const trade of rows){
    const instrument=pdf.splitTextToSize(String(trade.instrument||'—'),43);
    const rowHeight=Math.max(11,instrument.length*4+5);
    if(y+rowHeight>278)header();
    const r=tradeR(trade), result=trade.result||'OPEN';
    const value=isClosed(trade)&&trade.returnPct!==''&&trade.returnPct!=null?percent(number(trade.returnPct)).replace('−','-'):'—';
    pdf.setFontSize(9);pdf.setTextColor(palette.t2);
    pdf.text(trade.date||'—',15,y);pdf.text(instrument,45,y);pdf.text(trade.direction||'—',92,y);pdf.text(result,113,y);
    pdf.text(r==null?'—':`${r.toFixed(2)}R`,148,y);pdf.text(value,172,y);y+=rowHeight;
    pdf.setDrawColor(palette.s3);pdf.line(margin,y-5,pageW-margin,y-5);
  }
  if (signal?.aborted) throw new DOMException('Export cancelled.', 'AbortError');
  pdf.save(filename);
}
